// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { List, Loader2, ArrowLeft } from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import { VideoList, Video } from '../types';
import VideoGrid from './VideoGrid';
import { useNavigate } from 'react-router-dom';

interface SharedListPageProps {
  listId: string;
  onVideoClick: (video: Video) => void;
}

const SharedListPage: React.FC<SharedListPageProps> = ({ listId, onVideoClick }) => {
  const [videoList, setVideoList] = useState<VideoList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSharedList = async () => {
      setLoading(true);
      setError(null);
      
      // Fetch the list details
      const { data: listData, error: listError } = await supabase
        .from('video_lists')
        .select('*')
        .eq('id', listId)
        .single();

      if (listError || !listData) {
        console.error('Error fetching shared list:', listError);
        setError('Lista no encontrada o no accesible.');
        setLoading(false);
        return;
      }

      // Fetch the videos associated with the list
      const { data: listVideosData, error: listVideosError } = await supabase
        .from('list_videos') // Assuming a join table 'list_videos'
        .select(`
          videos (*)
        `)
        .eq('list_id', listId);

      if (listVideosError) {
        console.error('Error fetching videos for list:', listVideosError);
        setError('Error al cargar los videos de la lista.');
        setLoading(false);
        return;
      }

      const videos = listVideosData.map((item: any) => item.videos) as Video[];
      setVideoList({ ...listData, videos: videos } as VideoList);
      setLoading(false);
    };

    fetchSharedList();
  }, [listId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <Loader2 className="w-16 h-16 animate-spin text-blue-500" />
        <p className="ml-4 text-xl text-gray-700">Cargando lista...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 p-8">
        <div className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-3xl p-8 shadow-xl text-center">
          <List className="w-24 h-24 text-red-500 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">¡Ups! Algo salió mal.</h2>
          <p className="text-gray-600 text-lg mb-6">{error}</p>
          <motion.button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-blue-500 text-white rounded-xl font-semibold flex items-center justify-center mx-auto gap-2 hover:bg-blue-600 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ArrowLeft className="w-5 h-5" />
            Volver al Inicio
          </motion.button>
        </div>
      </div>
    );
  }

  if (!videoList) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 p-8">
        <div className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-3xl p-8 shadow-xl text-center">
          <List className="w-24 h-24 text-gray-500 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Lista no encontrada.</h2>
          <p className="text-gray-600 text-lg mb-6">Parece que esta lista no existe o ha sido eliminada.</p>
          <motion.button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-blue-500 text-white rounded-xl font-semibold flex items-center justify-center mx-auto gap-2 hover:bg-blue-600 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ArrowLeft className="w-5 h-5" />
            Volver al Inicio
          </motion.button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-8">
      <div className="container mx-auto px-4 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-3xl p-8 shadow-xl mb-8"
        >
          <motion.button
            onClick={() => navigate('/')}
            className="mb-6 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl font-semibold flex items-center gap-2 hover:bg-gray-200 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al Inicio
          </motion.button>

          <div className="flex items-center gap-4 mb-4">
            <List className="w-12 h-12 text-blue-500" />
            <div>
              <h1 className="text-4xl font-bold text-gray-900">{videoList.name}</h1>
              {videoList.description && (
                <p className="text-gray-600 text-lg mt-1">{videoList.description}</p>
              )}
              <p className="text-gray-500 text-sm mt-2">
                Creada por {videoList.user_id} el {new Date(videoList.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
          <p className="text-gray-700 mt-4">
            ¡Explora esta increíble colección de videos de comida compartida por un amigo!
          </p>
        </motion.div>

        {videoList.videos && videoList.videos.length > 0 ? (
          <VideoGrid 
            videos={videoList.videos} 
            onVideoClick={onVideoClick} 
            onDeleteVideo={() => {}} // No delete functionality in shared lists
            isLoading={false}
          />
        ) : (
          <div className="text-center py-16 bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-3xl shadow-xl">
            <List className="w-24 h-24 mx-auto mb-6 text-gray-400" />
            <h3 className="text-2xl font-bold text-gray-800 mb-3">
              Esta lista está vacía.
            </h3>
            <p className="text-gray-600 max-w-md mx-auto">
              Parece que aún no se han añadido videos a esta lista.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SharedListPage;